import { useEffect, useState, useMemo } from "react";
import { api } from "../api";
import { formatVND } from "../utils";
import {
  AreaChart,
  Area,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from "recharts";

interface ComparisonPoint {
  date: string;
  thisRevenue: number;
  prevRevenue: number;
  thisOrders: number;
  prevOrders: number;
}

interface ComparisonData {
  period: string;
  days: number;
  this_period: Array<{ date: string; revenue: number; orders: number }>;
  prev_period: Array<{ date: string; revenue: number; orders: number }>;
}

export function RevenueComparisonChart({ period }: { period: "week" | "month" }) {
  const [data, setData] = useState<ComparisonData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(`${api.baseUrl}/api/dashboard/comparison?period=${period}`, {
      headers: api.authHeaders(),
    })
      .then((r) => r.json())
      .then((d: ComparisonData) => {
        if (alive) setData(d);
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [period]);

  const chartData = useMemo<ComparisonPoint[]>(() => {
    if (!data) return [];
    // Align theo vị trí index: ngày N kỳ này vs ngày N kỳ trước (calendar boundary).
    const max = Math.max(data.this_period.length, data.prev_period.length);
    // ponytail: dùng weekday/ordinal label thay vì actual date → không lệch khi kỳ đang có partial data.
    const weekday = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"];
    return Array.from({ length: max }, (_, idx) => {
      const thisVal = data.this_period[idx];
      const prevVal = data.prev_period[idx];
      let label: string;
      if (period === "week") {
        // Label theo weekday (T2-CN) — lặp vòng tuần
        label = weekday[idx % 7];
      } else {
        // Tháng: số ngày trong tháng (1, 2, 3...)
        const dateStr = thisVal?.date || prevVal?.date || "";
        const parts = dateStr.split("-");
        label = parts[2] ? String(parseInt(parts[2], 10)) : String(idx + 1);
      }
      return {
        date: label,
        thisRevenue: thisVal?.revenue || 0,
        prevRevenue: prevVal?.revenue || 0,
        thisOrders: thisVal?.orders || 0,
        prevOrders: prevVal?.orders || 0,
      };
    });
  }, [data, period]);

  const totals = useMemo(() => {
    if (!data) return { thisTotal: 0, prevTotal: 0, pct: 0 };
    const thisTotal = data.this_period.reduce((s, d) => s + d.revenue, 0);
    const prevTotal = data.prev_period.reduce((s, d) => s + d.revenue, 0);
    const pct = prevTotal > 0 ? ((thisTotal - prevTotal) / prevTotal) * 100 : null;
    return { thisTotal, prevTotal, pct };
  }, [data]);

  const label = period === "week" ? "tuần" : "tháng";
  const prevLabel = period === "week" ? "Tuần trước" : "Tháng trước";
  const thisLabel = period === "week" ? "Tuần này" : "Tháng này";

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: "center", color: "var(--text-secondary)" }}>
        Đang tải so sánh…
      </div>
    );
  }

  if (!data || chartData.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: "center", color: "var(--text-secondary)" }}>
        Chưa có dữ liệu để so sánh
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      {/* Badge tổng kết */}
      <div style={{ position: "absolute", top: 8, right: 8, zIndex: 10 }}>
        <span
          style={{
            padding: "4px 12px",
            borderRadius: 20,
            fontSize: 12,
            fontWeight: 700,
            background: totals.pct !== null && totals.pct >= 0
              ? "rgba(52,211,153,0.12)"
              : "rgba(248,113,113,0.12)",
            color: totals.pct !== null && totals.pct >= 0 ? "#34d399" : "#f87171",
          }}
        >
          {totals.pct !== null
            ? `${totals.pct >= 0 ? "+" : ""}${totals.pct.toFixed(1)}%`
            : "—"} so với {label} trước
        </span>
      </div>

      <div style={{ height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="thisGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#34d399" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#34d399" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="prevGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#9ca3af" stopOpacity={0.15} />
                <stop offset="100%" stopColor="#9ca3af" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="date" stroke="#666" fontSize={10} />
            <YAxis
              stroke="#666"
              fontSize={10}
              tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`}
            />
            <Tooltip
              contentStyle={{
                background: "#0a0a0a",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 10,
              }}
              labelStyle={{ color: "#888" }}
              formatter={(v: number, name: string) => {
                if (name === thisLabel || name === prevLabel) return formatVND(v);
                return v;
              }}
            />
            <Legend />
            <Area
              type="monotone"
              dataKey="prevRevenue"
              name={prevLabel}
              stroke="#9ca3af"
              strokeWidth={1.5}
              strokeDasharray="6 3"
              fill="url(#prevGrad)"
              dot={false}
            />
            <Area
              type="monotone"
              dataKey="thisRevenue"
              name={thisLabel}
              stroke="#34d399"
              strokeWidth={2.5}
              fill="url(#thisGrad)"
              dot={{ fill: "#34d399", r: 2 }}
              activeDot={{ r: 5 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Tóm tắt */}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 12, color: "var(--text-secondary)" }}>
        <span>{thisLabel}: <strong style={{ color: "var(--text)" }}>{formatVND(totals.thisTotal)}</strong></span>
        <span>{prevLabel}: <strong style={{ color: "var(--text)" }}>{formatVND(totals.prevTotal)}</strong></span>
      </div>
    </div>
  );
}